import React, { useState, useEffect, useRef } from "react";
import { FaDownload, FaChevronLeft, FaChevronRight } from "react-icons/fa";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const rre_rules_book = `${process.env.REACT_APP_IMAGE_API_URL}/uploads/websiteDocumentUpload/`;

// ═══════════════════════════════════════════
// PDF VIEWER — HD / Retina Sharp + Mobile Side Scroll
// ═══════════════════════════════════════════
const PdfViewer = ({ pdfUrl }) => {
  const [pdfDocument, setPdfDocument] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [scale, setScale] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  const canvasRef = useRef(null);
  const renderTaskRef = useRef(null);

  // Detect mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Mobile pe chhota default scale
  useEffect(() => {
    if (isMobile) setScale(0.95);
    else setScale(1.2);
  }, [isMobile]);

  useEffect(() => {
    const loadPdfJs = () =>
      new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.min.js";
        script.onload = () => {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc =
            "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.4.120/pdf.worker.min.js";
          resolve();
        };
        script.onerror = reject;
        document.head.appendChild(script);
      });

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);
        await loadPdfJs();

        if (!pdfUrl) throw new Error("PDF URL is missing");

        const loadingTask = window.pdfjsLib.getDocument({
          url: pdfUrl,
          withCredentials: false,
          useSystemFonts: true,
          cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.4.120/cmaps/",
          cMapPacked: true,
        });

        const pdf = await loadingTask.promise;
        setPdfDocument(pdf);
        setPageCount(pdf.numPages);
        setLoading(false);
      } catch (err) {
        console.error("Error loading PDF:", err);
        if (
          err.message.includes("Missing PDF") ||
          err.message.includes("404")
        ) {
          setError("PDF file not found. The document may be unavailable.");
        } else if (err.message.includes("network")) {
          setError("Network error. Please check your connection.");
        } else {
          setError("Failed to load PDF document. Please try again later.");
        }
        setLoading(false);
      }
    };

    loadPdf();
  }, [pdfUrl]);

  useEffect(() => {
    if (pdfDocument && canvasRef.current) renderPage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfDocument, currentPage, scale]);

  // ═══════════ CORRECT HD RENDER ═══════════
  const renderPage = async () => {
    try {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel();
      }

      const page = await pdfDocument.getPage(currentPage);
      const canvas = canvasRef.current;
      const context = canvas.getContext("2d", { alpha: false });

      const outputScale = window.devicePixelRatio || 1;

      const viewport = page.getViewport({ scale });

      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = "high";

      const transform =
        outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null;

      renderTaskRef.current = page.render({
        canvasContext: context,
        viewport,
        transform,
      });

      await renderTaskRef.current.promise;
      renderTaskRef.current = null;
    } catch (err) {
      if (err?.name === "RenderingCancelledException") {
        // ignore
      } else {
        console.error("Error rendering page:", err);
        setError("Error displaying page. Please try again.");
      }
    }
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const zoomIn = () => setScale((p) => Math.min(p + 0.2, 3));
  const zoomOut = () => setScale((p) => Math.max(p - 0.2, 0.5));

  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const navBtnStyle = (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    height: isMobile ? "40px" : "38px",
    padding: isMobile ? "0 14px" : "0 16px",
    fontSize: isMobile ? "12.5px" : "13px",
    fontWeight: 600,
    color: disabled ? "#94a3b8" : "#475569",
    background: disabled ? "#f1f5f9" : "#ffffff",
    border: "1.5px solid #e2e8f0",
    borderRadius: "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    outline: "none",
    whiteSpace: "nowrap",
  });

  const zoomBtnStyle = (disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: isMobile ? "40px" : "34px",
    height: isMobile ? "40px" : "34px",
    padding: 0,
    fontSize: isMobile ? "20px" : "16px",
    fontWeight: 700,
    color: disabled ? "#94a3b8" : "#475569",
    background: disabled ? "#f1f5f9" : "#ffffff",
    border: "1.5px solid #e2e8f0",
    borderRadius: "8px",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
    flexShrink: 0,
  });

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "400px",
          gap: "16px",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading PDF...</span>
        </div>
        <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
          Loading PDF...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "400px",
          padding: "20px",
        }}
      >
        <div
          style={{
            maxWidth: "500px",
            padding: "24px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderLeft: "4px solid #dc2626",
            borderRadius: "10px",
            textAlign: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "#991b1b",
              fontSize: "14px",
              fontWeight: 500,
            }}
          >
            ⚠ {error}
          </p>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                marginTop: "12px",
                color: "#2563eb",
                fontSize: "13px",
                fontWeight: 600,
                textDecoration: "underline",
              }}
            >
              Try opening PDF directly →
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        borderRadius: "14px",
        overflow: "hidden",
        border: "1px solid #e2e8f0",
        background: "#ffffff",
        boxShadow: "0 4px 16px rgba(15, 23, 42, 0.06)",
      }}
    >
      {/* TOP NAVIGATION */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          padding: isMobile ? "12px" : "14px 18px",
          background: gradientBg,
          borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            style={navBtnStyle(currentPage <= 1)}
          >
            <FaChevronLeft size={11} /> Prev
          </button>

          <span
            style={{
              padding: isMobile ? "5px 10px" : "6px 14px",
              background: "rgba(255, 255, 255, 0.15)",
              borderRadius: "8px",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: 600,
              color: "#ffffff",
              backdropFilter: "blur(6px)",
              whiteSpace: "nowrap",
            }}
          >
            {currentPage} / {pageCount}
          </span>

          <button
            onClick={() =>
              handlePageChange(Math.min(pageCount, currentPage + 1))
            }
            disabled={currentPage >= pageCount}
            style={navBtnStyle(currentPage >= pageCount)}
          >
            Next <FaChevronRight size={11} />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            style={zoomBtnStyle(scale <= 0.5)}
          >
            −
          </button>

          <span
            style={{
              padding: isMobile ? "5px 8px" : "6px 12px",
              background: "rgba(255, 255, 255, 0.15)",
              borderRadius: "8px",
              fontSize: isMobile ? "11.5px" : "12.5px",
              fontWeight: 600,
              color: "#ffffff",
              backdropFilter: "blur(6px)",
              minWidth: isMobile ? "55px" : "70px",
              textAlign: "center",
            }}
          >
            {Math.round(scale * 100)}%
          </span>

          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            style={zoomBtnStyle(scale >= 3)}
          >
            +
          </button>
        </div>
      </div>

      {/* CANVAS AREA */}
      {/* CANVAS AREA */}
     {/* CANVAS AREA — No left-right scroll */}
<div
  style={{
    display: "flex",
    justifyContent: "center",
    padding: "16px 12px",
    background: "#f1f5f9",
    overflow: "auto",           // only vertical scroll
    maxHeight: "75vh",
    WebkitOverflowScrolling: "touch",
  }}
>
  <canvas
    ref={canvasRef}
    style={{
      boxShadow: "0 4px 20px rgba(15, 23, 42, 0.12)",
      borderRadius: "6px",
      background: "#fff",
      display: "block",
      maxWidth: "100%",         // important — prevents horizontal scroll
    }}
  />
</div>

      {/* BOTTOM NAVIGATION */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "10px",
          padding: isMobile ? "12px" : "14px 18px",
          background: "#f8fafc",
          borderTop: "1px solid #e2e8f0",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            style={navBtnStyle(currentPage <= 1)}
          >
            <FaChevronLeft size={11} /> Prev
          </button>

          <span
            style={{
              padding: isMobile ? "5px 10px" : "6px 14px",
              background: "#ffffff",
              border: "1.5px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: 600,
              color: "#475569",
              whiteSpace: "nowrap",
            }}
          >
            {currentPage} / {pageCount}
          </span>

          <button
            onClick={() =>
              handlePageChange(Math.min(pageCount, currentPage + 1))
            }
            disabled={currentPage >= pageCount}
            style={navBtnStyle(currentPage >= pageCount)}
          >
            Next <FaChevronRight size={11} />
          </button>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            onClick={zoomOut}
            disabled={scale <= 0.5}
            style={zoomBtnStyle(scale <= 0.5)}
          >
            −
          </button>
          <span
            style={{
              padding: isMobile ? "5px 8px" : "6px 12px",
              background: "#ffffff",
              border: "1.5px solid #e2e8f0",
              borderRadius: "8px",
              fontSize: isMobile ? "11.5px" : "12.5px",
              fontWeight: 600,
              color: "#475569",
              minWidth: isMobile ? "55px" : "70px",
              textAlign: "center",
            }}
          >
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            disabled={scale >= 3}
            style={zoomBtnStyle(scale >= 3)}
          >
            +
          </button>
        </div>
      </div>
    </div>
  );
};
// ═══════════════════════════════════════════
// PREVIOUS PAPERS COMPONENT
// ═══════════════════════════════════════════
const PreviousPapers = () => {
  const [baseurl, setBaseurl] = useState("");
  const [previousList, setPreviousList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPdf, setSelectedPdf] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalItems, setTotalItems] = useState(0);

  const itemsPerPage = 8;

  useEffect(() => {
    fetchPreviousPapers();
  }, []);

  const fetchPreviousPapers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/site-setting`);
      const responseData = res.data.data || res.data;

      if (responseData.rre_rules_book) {
        setPreviousList([
          {
            id: 1,
            title: "A2 Demo Real Estate Rules Book Details",
            pdf: responseData.rre_rules_book,
            created_at: responseData.created_at || new Date().toISOString(),
          },
        ]);
        setTotalItems(1);
      } else if (Array.isArray(responseData)) {
        setPreviousList(responseData);
        setTotalItems(responseData.length);
      } else if (responseData.papers && Array.isArray(responseData.papers)) {
        setPreviousList(responseData.papers);
        setTotalItems(responseData.papers.length);
      } else {
        const pdfKeys = Object.keys(responseData).filter(
          (key) =>
            key.toLowerCase().includes("pdf") ||
            key.toLowerCase().includes("paper")
        );

        if (pdfKeys.length > 0) {
          const papers = pdfKeys.map((key, index) => ({
            id: index + 1,
            title: key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase()),
            pdf: responseData[key],
            created_at: responseData.created_at || new Date().toISOString(),
          }));
          setPreviousList(papers);
          setTotalItems(papers.length);
        } else {
          setPreviousList([]);
          setTotalItems(0);
        }
      }

      setBaseurl(process.env.REACT_APP_IMAGE_API_URL);
      setError(null);
    } catch (error) {
      console.error("Error fetching previous papers:", error);
      setError("Failed to load previous papers. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (previousList.length > 0 && baseurl) {
      const pdfUrl = `${baseurl}/uploads/websiteDocumentUpload/${previousList[0].pdf}`;
      setSelectedPdf(pdfUrl);
    }
  }, [previousList, baseurl]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPapers = previousList.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          flexDirection: "column",
          gap: "16px",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p style={{ color: "#64748b", fontSize: "14px", margin: 0 }}>
          Loading PDF documents...
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          padding: "20px",
        }}
      >
        <div
          style={{
            padding: "20px 24px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderLeft: "4px solid #dc2626",
            borderRadius: "10px",
            color: "#991b1b",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          ⚠ {error}
        </div>
      </div>
    );
  }

  return (
    <section className="padding_15">
      <div
        style={{
          background: "#ffffff",
          borderRadius: "14px",
          overflow: "hidden",
          boxShadow: "0 4px 20px rgba(15, 23, 42, 0.06)",
        }}
      >
        {/* CARD HEADER */}
        <div
          style={{
            padding: "18px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
            background: gradientBg,
            borderBottom: "1px solid rgba(255, 255, 255, 0.08)",
            flexWrap: "wrap",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              minWidth: 0,
              flex: "1 1 auto",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "40px",
                height: "40px",
                minWidth: "40px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.15)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                color: "#ffffff",
                fontSize: "18px",
                backdropFilter: "blur(6px)",
                flexShrink: 0,
              }}
            >
              📄
            </div>
            <div style={{ minWidth: 0 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: "18px",
                  fontWeight: 700,
                  color: "#ffffff",
                  lineHeight: 1.3,
                  letterSpacing: "0.01em",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Rule Book
              </h3>
              <p
                style={{
                  margin: 0,
                  fontSize: "12.5px",
                  color: "rgba(255, 255, 255, 0.75)",
                  fontWeight: 400,
                }}
              >
                View and download rule book documents
              </p>
            </div>
          </div>

          {selectedPdf && (
            <a
              href={selectedPdf}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                padding: "9px 18px",
                fontSize: "13px",
                fontWeight: 700,
                color: "#1e1b4b",
                background: "#ffffff",
                border: "none",
                borderRadius: "10px",
                textDecoration: "none",
                cursor: "pointer",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                transition: "all 0.22s ease",
                flexShrink: 0,
              }}
            >
              <FaDownload size={13} />
              Download
            </a>
          )}
        </div>

        {/* CARD BODY */}
        <div style={{ padding: "24px" }}>
          {currentPapers.length > 0 ? (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                gap: "16px",
                marginBottom: "20px",
              }}
            >
              {currentPapers.map((paper, index) => (
                <div
                  key={paper.id || index}
                  style={{
                    padding: "16px",
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    cursor: "pointer",
                    transition: "all 0.25s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: "36px",
                        borderRadius: "8px",
                        background: gradientBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#ffffff",
                        fontSize: "16px",
                        flexShrink: 0,
                      }}
                    >
                      📄
                    </div>
                    <span
                      style={{
                        fontSize: "13.5px",
                        fontWeight: 600,
                        color: "#0f172a",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {paper.title}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                background: "#f8fafc",
                borderRadius: "12px",
                border: "1px dashed #cbd5e1",
                color: "#64748b",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              No previous papers available.
            </div>
          )}

          {totalPages > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
                marginBottom: "20px",
              }}
            >
              <nav>
                <ul
                  className="pagination"
                  style={{
                    display: "flex",
                    gap: "6px",
                    margin: 0,
                    listStyle: "none",
                    padding: 0,
                  }}
                >
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <FaChevronLeft size={11} /> Previous
                    </button>
                  </li>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <li
                        key={page}
                        className={`page-item ${currentPage === page ? "active" : ""}`}
                      >
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      </li>
                    )
                  )}
                  <li
                    className={`page-item ${currentPage === totalPages ? "disabled" : ""
                      }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next <FaChevronRight size={11} />
                    </button>
                  </li>
                </ul>
              </nav>
            </div>
          )}

          {selectedPdf && (
            <div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  marginBottom: "14px",
                  paddingBottom: "12px",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "20px",
                    borderRadius: "3px",
                    background: gradientBg,
                  }}
                />
                <h4
                  style={{
                    margin: 0,
                    fontSize: "15px",
                    fontWeight: 700,
                    color: "#1e1b4b",
                    letterSpacing: "0.01em",
                  }}
                >
                  Document Preview
                </h4>
              </div>
              <PdfViewer pdfUrl={selectedPdf} />
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default PreviousPapers;